import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import i18n, { SUPPORTED_LANGS, persistLang } from "@/i18n/i18n";
import { SessionTimeoutDialog } from "@/components/common/SessionTimeoutDialog";
import { markActivity, isRecentlyActive } from "@/lib/activity";

const AuthContext = createContext(null);

// Client-side session policy (backend enforces the real 8h cap + revocation).
const IDLE_LIMIT_MS = 30 * 60 * 1000;         // 30 min of real inactivity -> logout
const IDLE_WARN_MS = 2 * 60 * 1000;           // warn 2 min before (at 28 min)
const PROACTIVE_REFRESH_MS = 12 * 60 * 1000;  // refresh the 15-min access while active
const CHECK_MS = 15 * 1000;
const ACTIVITY_THROTTLE_MS = 3000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "input", "change", "click", "wheel"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [env, setEnv] = useState(null); // { is_production, label } — single source: backend APP_ENV
  const [timeoutWarn, setTimeoutWarn] = useState(false);
  const [warnSeconds, setWarnSeconds] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const lastThrottleRef = useRef(0);
  const warnRef = useRef(false);

  useEffect(() => {
    api.get("/meta/environment")
      .then((r) => setEnv(r.data))
      .catch(() => setEnv({ is_production: false, label: "Preview / Staging" }));
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      const lng = data.user?.preferred_language;
      if (lng && SUPPORTED_LANGS.includes(lng) && i18n.language !== lng) {
        i18n.changeLanguage(lng);
        persistLang(lng);
      }
    } catch (e) {
      // Only a genuine 401 means "not signed in". Network blips / backend restarts
      // must NOT drop an active session — keep the current user in that case.
      if (e?.response?.status === 401) setUser(false);
      else setUser((prev) => (prev && typeof prev === "object" ? prev : false));
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Step 1: email + password. super_admin gets a 2FA challenge instead of a session.
  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        return {
          ok: false,
          twoFactor: true,
          challenge: data.challenge, // "setup_2fa" | "2fa"
          preAuthToken: data.pre_auth_token,
        };
      }
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  // Step 2a (first enrollment): fetch QR + manual key using the pre-auth token.
  const setupTwoFactor = useCallback(async (preAuthToken) => {
    try {
      const { data } = await api.post("/auth/2fa/setup", {}, {
        headers: { Authorization: `Bearer ${preAuthToken}` },
      });
      return { ok: true, qr: data.qr_data_url, manualKey: data.manual_key };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  // Step 2b: submit the 6-digit code. On success the server sets the full session cookies.
  const verifyTwoFactor = useCallback(async (preAuthToken, code, challenge) => {
    const path = challenge === "setup_2fa" ? "/auth/2fa/verify-setup" : "/auth/2fa/verify";
    try {
      const { data } = await api.post(path, { code }, {
        headers: { Authorization: `Bearer ${preAuthToken}` },
      });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  // Step 1b (forced first login): set a new personal password, then continue to 2FA.
  const changePassword = useCallback(async (preAuthToken, newPassword) => {
    try {
      const { data } = await api.post("/auth/change-password", { new_password: newPassword }, {
        headers: { Authorization: `Bearer ${preAuthToken}` },
      });
      return { ok: true, challenge: data.challenge, preAuthToken: data.pre_auth_token };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  }, []);

  // Keep refs in sync for use inside stable event listeners / intervals.
  useEffect(() => { warnRef.current = timeoutWarn; }, [timeoutWarn]);

  // Explicit "stay signed in" from the warning dialog: refresh + reset idle clock.
  const stayLoggedIn = useCallback(async () => {
    markActivity();
    lastActivityRef.current = Date.now();
    setTimeoutWarn(false);
    try { await api.post("/auth/refresh", { active: true }); } catch { /* interceptor ends dead sessions */ }
  }, []);

  // End a session due to inactivity / 8h cap: refresh first (so the protected
  // /auth/logout can bump token_version and truly revoke), then log out.
  const endSession = useCallback(async () => {
    setTimeoutWarn(false);
    try { await api.post("/auth/refresh", { active: false }); } catch { /* 8h cap / revoked / inactive */ }
    await logout();
  }, [logout]);

  // Reset the idle clock whenever we enter an authenticated session.
  useEffect(() => {
    if (user && typeof user === "object") { lastActivityRef.current = Date.now(); markActivity(); }
  }, [user]);

  // Track real activity — mouse, keys, scroll AND typing/changes in long forms.
  useEffect(() => {
    const onActivity = () => {
      if (warnRef.current) return; // during the warning, require an explicit choice
      const n = Date.now();
      if (n - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return;
      lastThrottleRef.current = n;
      lastActivityRef.current = n;
      markActivity();
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
  }, []);

  // Idle + absolute-cap watchdog (only while authenticated).
  useEffect(() => {
    if (!(user && typeof user === "object")) return undefined;
    const id = setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current;
      const exp = user.session_expires_at ? Date.parse(user.session_expires_at) : null;
      if (exp && Date.now() >= exp) { endSession(); return; }
      if (idleFor >= IDLE_LIMIT_MS) { endSession(); return; }
      if (idleFor >= IDLE_LIMIT_MS - IDLE_WARN_MS) {
        setWarnSeconds(Math.max(0, Math.ceil((IDLE_LIMIT_MS - idleFor) / 1000)));
        setTimeoutWarn(true);
      } else {
        setTimeoutWarn((prev) => (prev ? false : prev));
      }
    }, CHECK_MS);
    return () => clearInterval(id);
  }, [user, endSession]);

  // Proactive silent refresh so active users never hit the 15-min access expiry.
  // Sends active=true only when the user genuinely interacted recently; otherwise
  // active=false so the server-side inactivity window keeps counting.
  useEffect(() => {
    if (!(user && typeof user === "object")) return undefined;
    const id = setInterval(() => {
      api.post("/auth/refresh", { active: isRecentlyActive() }).catch(() => { /* interceptor ends dead sessions */ });
    }, PROACTIVE_REFRESH_MS);
    return () => clearInterval(id);
  }, [user]);

  // Hard session end signalled by the API layer (refresh failed: revoked / 8h cap).
  useEffect(() => {
    const onEnded = () => setUser(false);
    window.addEventListener("auth:session-ended", onEnded);
    return () => window.removeEventListener("auth:session-ended", onEnded);
  }, []);

  // Apply UI language immediately (local + localStorage) and, when authenticated,
  // persist it as the user's preferred_language (User -> DE fallback).
  const changeLanguage = useCallback(async (lng) => {
    if (!SUPPORTED_LANGS.includes(lng)) return;
    await i18n.changeLanguage(lng);
    persistLang(lng);
    try {
      const { data } = await api.patch("/auth/me/language", { language: lng });
      setUser((prev) => (prev ? data.user : prev));
    } catch {
      /* anonymous/offline — local change already applied */
    }
  }, []);

  const can = useCallback(
    (module, action = "read") => !!(user && user.permissions?.[module]?.[action]),
    [user]
  );

  return (
    <AuthContext.Provider
      value={{ user, env, login, setupTwoFactor, verifyTwoFactor, changePassword, logout, changeLanguage, can, reload: loadMe, stayLoggedIn }}
    >
      {children}
      <SessionTimeoutDialog
        open={timeoutWarn}
        seconds={warnSeconds}
        onStay={stayLoggedIn}
        onLogout={endSession}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
